import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { sendCommand } from '../lib/extension'
import { useDraft } from '../hooks/useDraft'
import { useTemplates, type Template } from '../hooks/useTemplates'
import EmojiPicker from './EmojiPicker'
import GifPicker from './GifPicker'
import ComposeSyncButton from './ComposeSyncButton'
import { useConversation } from '../hooks/useConversations'

interface OptimisticMessage {
  clientId: string
  text: string
  previewUrl?: string
}

interface ReplyTo {
  messageUrn: string
  senderName: string
  senderProfileUrn: string
  text: string
}

interface ComposeBarProps {
  conversationUrn?: string | null
  participantUrns?: string[]
  salesNav?: boolean
  onPending?: (msg: OptimisticMessage) => void
  onSent?: (info?: { clientId: string; confirmedUrn?: string; conversationUrn?: string }) => void
  onSendError?: (msg: OptimisticMessage, error: string) => void
  replyTo?: ReplyTo | null
  onCancelReply?: () => void
  onToast?: (message: string, tone: 'success' | 'error' | 'info') => void
}

export default function ComposeBar({ conversationUrn, participantUrns, salesNav, onPending, onSent, onSendError, replyTo, onCancelReply, onToast }: ComposeBarProps) {
  const conversation = useConversation(conversationUrn ?? null)
  const { draft, saveDraft, clearDraft } = useDraft(conversationUrn ?? null)
  const { templates } = useTemplates()
  const [text, setText] = useState('')
  const sendingRef = useRef(false) // prevent double-send, but don't block UI
  const [slashOpen, setSlashOpen] = useState(false)
  const [slashQuery, setSlashQuery] = useState('')
  const [slashIndex, setSlashIndex] = useState(0)
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [attachmentUpload, setAttachmentUpload] = useState<{
    urn: string
    status: 'uploading' | 'ready' | 'error'
  } | null>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showGifPicker, setShowGifPicker] = useState(false)
  const [mentionOpen, setMentionOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const [mentionResults, setMentionResults] = useState<{ name: string; headline: string; profileUrn: string; profileImage: string | null }[]>([])
  const [mentionSearching, setMentionSearching] = useState(false)
  const [mentions, setMentions] = useState<{ name: string; profileUrn: string; start: number; length: number }[]>([])
  const mentionDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const gifAbortRef = useRef<AbortController | null>(null)

  // Close mention dropdown + abort GIF fetch when conversation changes
  useEffect(() => {
    setMentionOpen(false)
    setMentionResults([])
    setMentions([])
    return () => {
      gifAbortRef.current?.abort()
      gifAbortRef.current = null
    }
  }, [conversationUrn])

  // Only sync draft text on conversation change — not on every draft update (would clobber typing)
  const prevConvRef = useRef(conversationUrn)
  useEffect(() => {
    if (prevConvRef.current !== conversationUrn) {
      setText(draft)
      setMentions([])
      prevConvRef.current = conversationUrn
    }
  }, [conversationUrn, draft])

  // Re-measure textarea height on any text change (including programmatic template insertion)
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 240) + 'px'
  }, [text])

  const filteredTemplates = slashOpen
    ? templates.filter((t) => t.name.toLowerCase().startsWith(slashQuery.toLowerCase()))
    : []

  // Mention dropdown: conversation participants filtered by query (memoized to avoid infinite loops)
  const allParticipants = conversation?.allParticipants
  const mentionParticipants = useMemo(() => {
    if (!mentionOpen || !allParticipants) return []
    return allParticipants
      .filter((p) => p.name && p.profileUrn && p.name.toLowerCase().includes(mentionQuery.toLowerCase()))
      .map((p) => ({ name: p.name, headline: p.headline || '', profileUrn: p.profileUrn!, profileImage: p.profileImage }))
  }, [mentionOpen, mentionQuery, allParticipants])

  useEffect(() => {
    if (!mentionOpen) return
    if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current)
    if (!mentionQuery.trim()) { setMentionResults([]); setMentionSearching(false); return }
    setMentionSearching(true)
    mentionDebounceRef.current = setTimeout(() => {
      sendCommand<{ peers: { name: string; headline: string; profileUrn: string; profileImage: string | null }[] }>(
        'FETCH_PEER_SEARCH', { keywords: mentionQuery, count: 8 }
      ).then((r) => {
        const arr = Array.isArray(r?.peers) ? r.peers : []
        const partUrns = new Set(mentionParticipants.map((p) => p.profileUrn))
        setMentionResults(arr.filter((p) => !partUrns.has(p.profileUrn)))
      }).catch((err) => console.warn('[ComposeBar] Peer search failed:', err?.message))
        .finally(() => setMentionSearching(false))
    }, 300)
    return () => { if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current) }
  }, [mentionQuery, mentionOpen, mentionParticipants])

  const mentionItems = useMemo(
    () => [...mentionParticipants, ...mentionResults],
    [mentionParticipants, mentionResults]
  )

  const mentionItemCount = mentionItems.length
  useEffect(() => { setMentionIndex(0) }, [mentionItemCount])

  const handleChange = (value: string) => {
    // Recalculate mention positions based on text diff
    const oldText = text
    if (mentions.length > 0 && value !== oldText) {
      setMentions((prev) => {
        // Find where the edit happened by comparing old vs new
        let diffStart = 0
        while (diffStart < oldText.length && diffStart < value.length && oldText[diffStart] === value[diffStart]) diffStart++
        const lengthDelta = value.length - oldText.length

        return prev
          .map((m) => {
            // If edit is inside a mention's range, invalidate it
            if (diffStart >= m.start && diffStart < m.start + m.length) return null
            // If edit is before a mention, shift its start
            if (diffStart <= m.start) return { ...m, start: m.start + lengthDelta }
            return m
          })
          .filter((m): m is NonNullable<typeof m> => m !== null)
      })
    }

    setText(value)
    saveDraft(value)

    // Slash command detection
    const slashMatch = value.match(/\/(\w*)$/)
    if (slashMatch) {
      setSlashOpen(true)
      setSlashQuery(slashMatch[1])
      setSlashIndex(0)
      setMentionOpen(false)
    } else {
      setSlashOpen(false)
    }

    // @ mention detection: trigger on @query at end of text.
    // Only match if there's no space in the query (once you type a space after @Name, you've
    // moved on to regular text). Exception: spaces ARE allowed while actively building a query
    // to match multi-word names, but only up until a mention is inserted.
    if (!slashMatch) {
      // Find the last @ in the text
      const lastAtIdx = value.lastIndexOf('@')
      if (lastAtIdx >= 0) {
        const afterAt = value.slice(lastAtIdx + 1)
        // Check if this @ belongs to an already-tracked mention
        const isTracked = mentions.some((m) => m.start === lastAtIdx)
        // Check if there's content AFTER a tracked mention (user typed past it)
        const mentionAtPos = mentions.find((m) => m.start <= lastAtIdx && lastAtIdx < m.start + m.length)

        if (!isTracked && !mentionAtPos) {
          setMentionOpen(true)
          setMentionQuery(afterAt.trim())
          setMentionIndex(0)
        } else {
          setMentionOpen(false)
        }
      } else {
        setMentionOpen(false)
      }
    }
  }

  const insertTemplate = useCallback(
    (template: Template) => {
      let resolved = template.body
      if (conversation) {
        const parts = conversation.participantName.split(' ')
        const firstName = parts[0] || ''
        const lastName = parts.slice(1).join(' ') || ''
        resolved = resolved
          .replace(/\{\{first_name\}\}/g, firstName)
          .replace(/\{\{last_name\}\}/g, lastName)
          .replace(/\{\{full_name\}\}/g, conversation.participantName)
          .replace(/\{\{company\}\}/g, extractCompany(conversation.participantHeadline))
          .replace(/\{\{title\}\}/g, extractTitle(conversation.participantHeadline))
      }
      const newText = text.replace(/\/\w*$/, resolved)
      setText(newText)
      saveDraft(newText)
      setSlashOpen(false)
      inputRef.current?.focus()
    },
    [text, conversation, saveDraft]
  )

  const insertMention = useCallback(
    (person: { name: string; profileUrn: string }) => {
      // Replace the @query at end of text with @FullName
      const mentionText = `@${person.name}`
      const newText = text.replace(/@[^@]*$/, mentionText + ' ')
      const insertStart = newText.indexOf(mentionText) >= 0
        ? newText.lastIndexOf(mentionText)
        : newText.length - mentionText.length - 1
      setText(newText)
      saveDraft(newText)
      setMentions((prev) => [...prev, {
        name: person.name,
        profileUrn: person.profileUrn,
        start: insertStart,
        length: mentionText.length,
      }])
      setMentionOpen(false)
      setMentionResults([])
      setTimeout(() => {
        const pos = insertStart + mentionText.length + 1
        inputRef.current?.setSelectionRange(pos, pos)
        inputRef.current?.focus()
      }, 0)
    },
    [text, saveDraft]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Slash command navigation
    if (slashOpen && filteredTemplates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIndex((i) => Math.min(i + 1, filteredTemplates.length - 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSlashIndex((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertTemplate(filteredTemplates[slashIndex]); return }
      if (e.key === 'Escape') { setSlashOpen(false); return }
    }
    // Mention navigation
    if (mentionOpen && mentionItems.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((i) => Math.min(i + 1, mentionItems.length - 1)); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((i) => Math.max(i - 1, 0)); return }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionItems[mentionIndex]); return }
      if (e.key === 'Escape') { setMentionOpen(false); return }
    }
    // Two-step backspace on mentions
    if (e.key === 'Backspace' && mentions.length > 0) {
      const cursor = inputRef.current?.selectionStart ?? 0
      const selEnd = inputRef.current?.selectionEnd ?? cursor
      if (cursor === selEnd) { // no selection — check if cursor is at end of a mention
        const mention = mentions.find((m) => cursor === m.start + m.length)
        if (mention) {
          e.preventDefault()
          const firstName = mention.name.split(' ')[0]
          const fullMentionText = `@${mention.name}`
          const firstMentionText = `@${firstName}`
          if (fullMentionText !== firstMentionText && text.slice(mention.start, mention.start + mention.length) === fullMentionText) {
            // Step 1: shorten to first name
            const newText = text.slice(0, mention.start) + firstMentionText + text.slice(mention.start + mention.length)
            setText(newText)
            saveDraft(newText)
            setMentions((prev) => prev.map((m) =>
              m === mention ? { ...m, name: firstName, length: firstMentionText.length } : m
            ))
            setTimeout(() => inputRef.current?.setSelectionRange(mention.start + firstMentionText.length, mention.start + firstMentionText.length), 0)
          } else {
            // Step 2: remove mention entirely
            const newText = text.slice(0, mention.start) + text.slice(mention.start + mention.length)
            setText(newText)
            saveDraft(newText)
            setMentions((prev) => prev.filter((m) => m !== mention))
            setTimeout(() => inputRef.current?.setSelectionRange(mention.start, mention.start), 0)
          }
          return
        }
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSend() }
  }

  const handleSend = async () => {
    const trimmed = text.trim()
    const canSend = conversationUrn || (participantUrns && participantUrns.length > 0)
    if ((!trimmed && !attachmentFile) || !canSend || sendingRef.current) return
    sendingRef.current = true

    const optimistic: OptimisticMessage = {
      clientId: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: trimmed,
    }

    // Instant: show message, clear input, refocus — all synchronous, before any network call
    // Show pending for ALL send types — text, attachments, and GIFs
    onPending?.({
      ...optimistic,
      text: attachmentFile ? (trimmed || `Sending ${attachmentFile.name}...`) : trimmed,
    })
    // Capture mentions before clearing
    const currentMentions = mentions.length > 0 ? mentions.map((m) => {
      const urn = `urn:li:fsd_profile:${m.profileUrn}`
      return {
        start: m.start,
        length: m.length,
        attributeKindUnion: { entity: { urn } },
        type: { "com.linkedin.pemberly.text.Entity": { urn } },
        attributeKind: { entity: { urn } },
      }
    }) : []

    setText('')
    clearDraft() // Clear draft immediately so inbox doesn't show it as amber
    setMentions([])
    setMentionOpen(false)
    setSlashOpen(false)
    onCancelReply?.()
    inputRef.current?.focus()

    // Fire-and-forget: send in background, don't block the UI
    const currentAttachment = attachmentFile
    const currentUpload = attachmentUpload
    if (currentAttachment) {
      setAttachmentFile(null)
      setAttachmentUpload(null)
    }

    ;(async () => {
      try {
        let confirmedUrn: string | undefined
        let newConversationUrn: string | undefined
        if (currentAttachment && currentUpload?.status === 'ready' && currentUpload.urn) {
          // Upload already done — just send the message with the pre-uploaded URN
          const result = await sendCommand<{ message: { backendUrn?: string; backendConversationUrn?: string } }>(
            'SEND_MESSAGE_WITH_ATTACHMENT',
            {
              conversationUrn: conversationUrn ?? null,
              text: trimmed,
              attachmentUrn: currentUpload.urn,
              participantUrns: participantUrns || null,
              mediaType: currentAttachment.type.startsWith('image/') ? 'image' : 'file',
              filename: currentAttachment.name,
            }
          )
          confirmedUrn = result?.message?.backendUrn
          newConversationUrn = result?.message?.backendConversationUrn
        } else if (currentAttachment) {
          // Upload not ready (still in progress or failed) — fall back to full upload-then-send
          const result = await sendWithAttachment(conversationUrn ?? null, trimmed, currentAttachment, participantUrns)
          confirmedUrn = result?.message?.backendUrn
          newConversationUrn = result?.message?.backendConversationUrn
        } else {
          const result = await sendCommand<{ message: { backendUrn?: string; backendConversationUrn?: string } }>('SEND_MESSAGE', {
            conversationUrn: conversationUrn ?? null,
            text: trimmed,
            participantUrns: participantUrns || null,
            attributes: currentMentions.length > 0 ? currentMentions : undefined,
            salesNav: salesNav || undefined,
            replyToMessageUrn: replyTo?.messageUrn || null,
            replyToSenderUrn: replyTo?.senderProfileUrn || null,
          })
          confirmedUrn = result?.message?.backendUrn
          newConversationUrn = result?.message?.backendConversationUrn
        }
        onSent?.({ clientId: optimistic.clientId, confirmedUrn, conversationUrn: newConversationUrn || conversationUrn || undefined })
      } catch (err) {
        console.error('Send failed:', err)
        // Restore draft so user doesn't lose their text
        setText(trimmed)
        saveDraft(trimmed)
        onSendError?.(optimistic, String(err))
      } finally {
        sendingRef.current = false
      }
    })()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = '' // reset so same file can be re-selected
    setAttachmentFile(file)
    setAttachmentUpload({ urn: '', status: 'uploading' })

    // Start upload immediately — just puts bytes on LinkedIn's CDN, no message sent.
    // If user removes the attachment before send, the orphaned upload expires harmlessly.
    ;(async () => {
      try {
        const uploadType = file.type.startsWith('image/')
          ? 'MESSAGING_PHOTO_ATTACHMENT'
          : 'MESSAGING_FILE_ATTACHMENT'

        const meta = await sendCommand<{
          singleUploadUrl: string
          headers: Record<string, string>
          urn: string
        }>('UPLOAD_FILE_META', {
          filename: file.name,
          fileSize: file.size,
          mediaUploadType: uploadType,
        })

        if (!meta?.singleUploadUrl || !meta?.urn) {
          throw new Error('Upload metadata missing')
        }

        const buffer = await file.arrayBuffer()
        const base64 = btoa(
          new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        )

        await sendCommand('UPLOAD_FILE', {
          uploadUrl: meta.singleUploadUrl,
          fileBase64: base64,
          fileType: file.type,
          uploadHeaders: meta.headers || {},
        })

        setAttachmentUpload({ urn: meta.urn, status: 'ready' })
      } catch (err) {
        console.error('File upload failed:', err)
        setAttachmentUpload({ urn: '', status: 'error' })
      }
    })()
  }

  const openSlashMenu = () => {
    const newText = text + '/'
    setText(newText)
    saveDraft(newText)
    setSlashOpen(true)
    setSlashQuery('')
    setSlashIndex(0)
    inputRef.current?.focus()
  }

  return (
    <div className="relative shrink-0 border-t border-zinc-100 bg-white px-6 pb-3 pt-2.5">
      {/* Slash command dropdown */}
      {slashOpen && filteredTemplates.length > 0 && (
        <div className="absolute bottom-full left-6 right-6 mb-2 max-h-48 overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-lg">
          <div className="px-3.5 pb-1.5 pt-2.5">
            <span className="text-[10px] font-medium tracking-wider text-zinc-400">TEMPLATES</span>
          </div>
          {filteredTemplates.map((t, i) => (
            <button
              key={t.id}
              onMouseDown={(e) => { e.preventDefault(); insertTemplate(t) }}
              className={`flex w-full flex-col px-3.5 py-2 text-left ${
                i === slashIndex ? 'bg-zinc-100' : 'hover:bg-zinc-50'
              }`}
            >
              <span className="text-[13px] font-semibold text-zinc-900">/{t.name}</span>
              <span className="truncate text-xs text-zinc-500">{t.body}</span>
            </button>
          ))}
        </div>
      )}

      {/* @ Mention dropdown */}
      {mentionOpen && (mentionItems.length > 0 || mentionSearching) && (
        <div className="absolute bottom-full left-6 right-6 mb-2 max-h-56 overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-lg">
          {mentionParticipants.length > 0 && (
            <>
              <div className="px-3.5 pb-1 pt-2.5">
                <span className="text-[10px] font-medium tracking-wider text-zinc-400">IN THIS CONVERSATION</span>
              </div>
              {mentionParticipants.map((p, i) => (
                <button
                  key={p.profileUrn}
                  onMouseDown={(e) => { e.preventDefault(); insertMention(p) }}
                  onMouseEnter={() => setMentionIndex(i)}
                  className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left ${
                    i === mentionIndex ? 'bg-zinc-50' : ''
                  }`}
                >
                  {p.profileImage ? (
                    <img src={p.profileImage} alt="" className="h-6 w-6 shrink-0 rounded-full" />
                  ) : (
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[8px] font-semibold text-zinc-500">
                      {p.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="text-[12px] font-medium text-zinc-900">{p.name}</span>
                    {p.headline && <span className="ml-1.5 text-[11px] text-zinc-400">{p.headline}</span>}
                  </div>
                </button>
              ))}
            </>
          )}
          {mentionResults.length > 0 && (
            <>
              <div className="px-3.5 pb-1 pt-2.5">
                <span className="text-[10px] font-medium tracking-wider text-zinc-400">CONNECTIONS</span>
              </div>
              {mentionResults.map((p, rawI) => {
                const i = mentionParticipants.length + rawI
                return (
                  <button
                    key={p.profileUrn}
                    onMouseDown={(e) => { e.preventDefault(); insertMention(p) }}
                    onMouseEnter={() => setMentionIndex(i)}
                    className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left ${
                      i === mentionIndex ? 'bg-zinc-50' : ''
                    }`}
                  >
                    {p.profileImage ? (
                      <img src={p.profileImage} alt="" className="h-6 w-6 shrink-0 rounded-full" />
                    ) : (
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[8px] font-semibold text-zinc-500">
                        {p.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <span className="text-[12px] font-medium text-zinc-900">{p.name}</span>
                      {p.headline && <span className="ml-1.5 text-[11px] text-zinc-400">{p.headline}</span>}
                    </div>
                  </button>
                )
              })}
            </>
          )}
          {mentionSearching && mentionResults.length === 0 && (
            <>
              <div className="px-3.5 pb-1 pt-2.5">
                <span className="text-[10px] font-medium tracking-wider text-zinc-400">CONNECTIONS</span>
              </div>
              {[0.8, 0.5, 0.3].map((opacity, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3.5 py-2" style={{ opacity }}>
                  <div className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-zinc-200" />
                  <div className="flex flex-1 flex-col gap-1">
                    <div className="h-2.5 animate-pulse rounded bg-zinc-200" style={{ width: `${40 + i * 15}%` }} />
                    <div className="h-2 animate-pulse rounded bg-zinc-100" style={{ width: `${55 + i * 10}%` }} />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Reply-to bar */}
      {replyTo && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border-l-2 border-blue-400 bg-zinc-50 px-3 py-2">
          <div className="min-w-0 flex-1">
            <span className="text-[11px] font-medium text-blue-600">
              Replying to {replyTo.senderName || 'message'}
            </span>
            <p className="truncate text-xs text-zinc-500">
              {replyTo.text.slice(0, 80)}{replyTo.text.length > 80 ? '...' : ''}
            </p>
          </div>
          <button onClick={onCancelReply} className="shrink-0 text-zinc-400 hover:text-zinc-600">
            <span className="text-sm">×</span>
          </button>
        </div>
      )}

      {/* Attachment preview */}
      {attachmentFile && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-zinc-50 px-3 py-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#71717a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          <span className="flex-1 truncate text-xs text-zinc-600">{attachmentFile.name}</span>
          {attachmentUpload?.status === 'uploading' ? (
            <span className="text-[10px] text-blue-500">Uploading...</span>
          ) : attachmentUpload?.status === 'error' ? (
            <span className="text-[10px] text-red-500">Failed</span>
          ) : (
            <span className="text-[10px] text-zinc-400">{formatFileSize(attachmentFile.size)}</span>
          )}
          <button onClick={() => { setAttachmentFile(null); setAttachmentUpload(null) }} className="text-zinc-400 hover:text-zinc-600">
            <span className="text-sm">×</span>
          </button>
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-2.5">
        <div className="relative flex flex-1 items-center rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5">
          {/* Styled backdrop: mirrors textarea text with bold mentions */}
          {mentions.length > 0 && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-3.5 py-2.5 text-[13px] leading-relaxed text-transparent"
              style={{ maxHeight: '240px' }}
            >
              {renderComposeMentions(text, mentions)}
            </div>
          )}
          <textarea
            ref={inputRef}
            data-compose-textarea=""
            value={text}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => { setTimeout(() => { setMentionOpen(false) }, 150) }}
            placeholder="Type a message..."
            rows={1}
            className={`flex-1 resize-none bg-transparent text-[13px] leading-relaxed placeholder:text-zinc-400 focus:outline-none ${
              mentions.length > 0 ? 'text-transparent caret-zinc-900' : 'text-zinc-900'
            }`}
            style={{ maxHeight: '240px', transition: 'height 120ms ease-out' }}
            onInput={(e) => {
              const el = e.currentTarget
              el.style.height = 'auto'
              el.style.height = Math.min(el.scrollHeight, 240) + 'px'
            }}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={(!text.trim() && !attachmentFile) || (!conversationUrn && (!participantUrns || participantUrns.length === 0))}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-30"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>

      {/* Emoji picker */}
      {showEmojiPicker && (
        <EmojiPicker
          onSelect={(emoji) => {
            setText((prev) => {
              const updated = prev + emoji
              saveDraft(updated)
              return updated
            })
            setShowEmojiPicker(false)
            inputRef.current?.focus()
          }}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}

      {/* GIF picker */}
      {showGifPicker && (
        <GifPicker
          onSelect={async (gifUrl) => {
            setShowGifPicker(false)
            const gifOptimistic: OptimisticMessage = {
              clientId: `gif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              text: '',
              previewUrl: gifUrl,
            }
            onPending?.(gifOptimistic)
            // Abort any previous in-flight GIF fetch
            gifAbortRef.current?.abort()
            const controller = new AbortController()
            gifAbortRef.current = controller
            try {
              const resp = await fetch(gifUrl, { signal: controller.signal })
              const blob = await resp.blob()
              const file = new File([blob], 'giphy.gif', { type: 'image/gif' })
              if (controller.signal.aborted) return
              const result = await sendWithAttachment(conversationUrn ?? null, '', file, participantUrns)
              onSent?.({ clientId: gifOptimistic.clientId, confirmedUrn: result?.message?.backendUrn, conversationUrn: result?.message?.backendConversationUrn || conversationUrn || undefined })
            } catch (err) {
              if ((err as Error).name === 'AbortError') return // silently ignore aborted fetches
              console.error('GIF send failed:', err)
              onSendError?.(gifOptimistic, String(err))
            } finally {
              if (gifAbortRef.current === controller) gifAbortRef.current = null
            }
          }}
          onClose={() => setShowGifPicker(false)}
        />
      )}

      {/* Action buttons */}
      <div className="mt-1.5 flex items-center gap-0.5">
        <ActionBtn title="Emoji" onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowGifPicker(false) }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </ActionBtn>
        <ActionBtn title="Attach file" onClick={() => fileInputRef.current?.click()}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </ActionBtn>
        <ActionBtn title="GIF" onClick={() => { setShowGifPicker(!showGifPicker); setShowEmojiPicker(false) }}>
          <span className="text-[11px] font-bold tracking-tight text-zinc-400">GIF</span>
        </ActionBtn>
        <ComposeSyncButton
          conversationUrn={conversationUrn ?? null}
          participantProfileUrn={conversation?.participantProfileUrn ?? null}
          isGroup={conversation?.isGroup ?? false}
          onToast={onToast}
        />
        <div className="mx-1 h-3.5 w-px bg-zinc-200" />
        <button
          onClick={openSlashMenu}
          title="Templates"
          className="flex h-[30px] items-center rounded-md bg-zinc-100 px-2 text-xs font-semibold text-zinc-500 hover:bg-zinc-200"
        >
          /
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  )
}

async function sendWithAttachment(conversationUrn: string | null, text: string, file: File, participantUrns?: string[]) {
  // Step 1: Get upload URL
  const uploadType = file.type.startsWith('image/')
    ? 'MESSAGING_PHOTO_ATTACHMENT'
    : 'MESSAGING_FILE_ATTACHMENT'

  const meta = await sendCommand<{
    singleUploadUrl: string
    headers: Record<string, string>
    urn: string
  }>('UPLOAD_FILE_META', {
    filename: file.name,
    fileSize: file.size,
    mediaUploadType: uploadType,
  })

  if (!meta?.singleUploadUrl || !meta?.urn) {
    throw new Error('Upload metadata missing — LinkedIn API response format may have changed')
  }

  // Step 2: Upload bytes
  const buffer = await file.arrayBuffer()
  const base64 = btoa(
    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  )

  await sendCommand('UPLOAD_FILE', {
    uploadUrl: meta.singleUploadUrl,
    fileBase64: base64,
    fileType: file.type,
    uploadHeaders: meta.headers || {},
  })

  // Step 3: Send message with attachment
  return sendCommand<{ message: { backendUrn?: string; backendConversationUrn?: string } }>('SEND_MESSAGE_WITH_ATTACHMENT', {
    conversationUrn,
    text,
    attachmentUrn: meta.urn,
    participantUrns: participantUrns || null,
    mediaType: file.type.startsWith('image/') ? 'image' : 'file',
    filename: file.name,
  })
}

function ActionBtn({ children, title, onClick }: { children: React.ReactNode; title: string; onClick?: () => void }) {
  return (
    <button title={title} onClick={onClick} className="flex h-[30px] w-[30px] items-center justify-center rounded-md hover:bg-zinc-100">
      {children}
    </button>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

const HEADLINE_SEPARATOR = /\s+(?:at|@)\s+|\s*[|·—–-]\s*/i

function extractCompany(headline: string): string {
  if (!headline) return ''
  // Try "at/@ Company" first
  const atMatch = headline.match(/(?:at|@)\s+(.+?)(?:\s*[|·—–,\-]|$)/i)
  if (atMatch?.[1]) return atMatch[1].trim()
  // Fall back to second segment after any separator
  const parts = headline.split(HEADLINE_SEPARATOR)
  return parts.length > 1 ? parts[1].trim() : ''
}

function extractTitle(headline: string): string {
  if (!headline) return ''
  const parts = headline.split(HEADLINE_SEPARATOR)
  return parts[0]?.trim() ?? ''
}

// Renders compose text with highlighted mention names (backdrop behind transparent textarea).
// Uses background color instead of font-weight to avoid width mismatch with the textarea.
function renderComposeMentions(
  text: string,
  mentions: { start: number; length: number }[]
): (string | JSX.Element)[] {
  if (!mentions.length) return [<span key="all" className="text-zinc-900">{text}</span>]
  const sorted = [...mentions].sort((a, b) => a.start - b.start)
  const parts: (string | JSX.Element)[] = []
  let last = 0
  for (const m of sorted) {
    if (m.start > last) parts.push(<span key={`t${last}`} className="text-zinc-900">{text.slice(last, m.start)}</span>)
    parts.push(
      <span key={`m${m.start}`} className="rounded-sm bg-blue-100 text-blue-700">
        {text.slice(m.start, m.start + m.length)}
      </span>
    )
    last = m.start + m.length
  }
  if (last < text.length) parts.push(<span key={`t${last}`} className="text-zinc-900">{text.slice(last)}</span>)
  return parts
}
